import React, {useMemo, useState} from "react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import Chart from "react-google-charts";
import {BarChart3, Download, Filter, Package, Tag, X} from "lucide-react";
import Button from "../../Common/Button";
import DeltaBadge from "../components/DeltaBadge";
import {formatCurrency, formatNumber, formatPercent} from "../dashboard/formatters";
import {toLocalDateStr} from "../../../utils/dateHelpers";
import {downloadCsv, fetchProductStats, selectionIds, statsToCsv} from "./productStatsApi";
import {SelectionChips} from "./ProductFilterPicker";

// Résumé d'Activité quand un filtre « Produits / Catégories » est actif :
// KPI nets des remises (avec comparaison de période), courbe par jour,
// détail par produit et par catégorie, export CSV.

const rangeKey = (range) => (range ? `${toLocalDateStr(range.startDate)}_${toLocalDateStr(range.endDate)}` : "none");

const useProductStats = (range, startTime, endTime, selection, refreshKey) => {
    const ids = selectionIds(selection);
    return useQuery({
        queryKey: ["activity-product-stats", rangeKey(range), startTime, endTime, ids.productIds, ids.categoryIds, refreshKey],
        queryFn: () => fetchProductStats({range, startTime, endTime, selection}),
        enabled: !!range?.startDate && !!range?.endDate && selection.length > 0,
        placeholderData: keepPreviousData,
        staleTime: 60_000,
    });
};

const shortDate = (iso) => {
    const [, m, d] = String(iso).split("-");
    return d && m ? `${d}/${m}` : iso;
};

const Kpi = ({label, value, previous, format, invert, hint, isDarkMode}) => (
    <div data-testid="product-kpi"
         className={`flex min-h-[100px] flex-col items-center justify-center p-4 text-center ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
        <p className={`text-xs uppercase ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{label}</p>
        <p className={`mt-1 text-xl font-bold tabular-nums ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}>{format(value)}</p>
        {hint && <p className="mt-0.5 text-[11px] text-gray-400">{hint}</p>}
        {previous !== undefined && (
            <DeltaBadge current={value} previous={previous} formatValue={format} invert={invert}
                        isDarkMode={isDarkMode} align="center"/>
        )}
    </div>
);

const ShareBar = ({value, isDarkMode}) => (
    <div className="flex items-center gap-2">
        <div className={`h-1.5 w-16 overflow-hidden ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>
            <div className="h-full bg-primary" style={{width: `${Math.min(100, Math.max(0, value))}%`}}/>
        </div>
        <span className="w-12 text-right tabular-nums">{formatPercent(value)}</span>
    </div>
);

const StatsTable = ({rows, columns, isDarkMode, emptyLabel}) => (
    <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead className={`border-b ${isDarkMode ? "border-gray-700 bg-gray-700/50" : "border-gray-200 bg-gray-50"}`}>
            <tr className={`text-left text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {columns.map((c) => (
                    <th key={c.key} className={`px-3 py-2 font-medium ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
                ))}
            </tr>
            </thead>
            <tbody>
            {rows.map((r) => (
                <tr key={r.id || r.name} className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                    {columns.map((c) => (
                        <td key={c.key} className={`px-3 py-2.5 ${c.align === "right" ? "text-right tabular-nums" : ""}`}>
                            {c.render ? c.render(r) : r[c.key]}
                        </td>
                    ))}
                </tr>
            ))}
            {rows.length === 0 && (
                <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-gray-500">{emptyLabel}</td></tr>
            )}
            </tbody>
        </table>
    </div>
);

const ProductStatsView = ({selection, onChange, dateRange, startTime, endTime, comparisonRange, refreshKey, isDarkMode}) => {
    const {t} = useTranslation("activite");
    const [tableTab, setTableTab] = useState("products");
    const range = dateRange?.[0];
    const current = useProductStats(range, startTime, endTime, selection, refreshKey);
    const previous = useProductStats(comparisonRange?.[0], startTime, endTime, comparisonRange ? selection : [], refreshKey);

    const stats = current.data;
    const s = stats?.summary;
    const p = comparisonRange ? previous.data?.summary : undefined;
    const prev = (field) => (p ? (p[field] ?? 0) : undefined);

    const chartData = useMemo(() => {
        const timeline = stats?.timeline || [];
        if (!timeline.length) return null;
        return [
            [t("productFilter.chart.day"), t("productFilter.kpi.net"), t("productFilter.kpi.quantity")],
            ...timeline.map((d) => [shortDate(d.date), Number(d.net) || 0, Number(d.quantity) || 0]),
        ];
    }, [stats, t]);

    const exportCsv = () => {
        if (!stats) return;
        const start = toLocalDateStr(range.startDate);
        const end = toLocalDateStr(range.endDate);
        downloadCsv(statsToCsv(stats, {startDate: start, endDate: end}), `stats-produits_${start}_${end}.csv`);
    };

    // Blocs pleine largeur, carrés, séparés par des filets (style du Résumé).
    const line = isDarkMode ? "border-gray-700" : "border-gray-200";
    const card = `border-b ${line} ${isDarkMode ? "bg-gray-800" : "bg-white"}`;
    const money = (v) => formatCurrency(v);

    const productColumns = [
        {key: "name", label: t("productFilter.table.product"), render: (r) => <span className="font-medium">{r.name}</span>},
        {key: "category_name", label: t("productFilter.table.category"), render: (r) => r.category_name || "—"},
        {key: "quantity", label: t("productFilter.table.quantity"), align: "right", render: (r) => formatNumber(r.quantity)},
        {key: "gross", label: t("productFilter.table.gross"), align: "right", render: (r) => money(r.gross)},
        {key: "discount", label: t("productFilter.table.discount"), align: "right",
            render: (r) => (r.discount > 0 ? <span className="text-red-500">−{money(r.discount)}</span> : "—")},
        {key: "net", label: t("productFilter.table.net"), align: "right", render: (r) => <span className="font-semibold">{money(r.net)}</span>},
        {key: "tickets", label: t("productFilter.table.tickets"), align: "right", render: (r) => formatNumber(r.tickets)},
        {key: "share", label: t("productFilter.table.share"), align: "right", render: (r) => <ShareBar value={r.share} isDarkMode={isDarkMode}/>},
    ];
    const categoryColumns = [
        {key: "name", label: t("productFilter.table.category"), render: (r) => <span className="font-medium">{r.name}</span>},
        {key: "products_count", label: t("productFilter.table.products"), align: "right"},
        ...productColumns.slice(2),
    ];

    return (
        <section data-testid="product-stats-view" className={`border-t ${line}`}>
            {/* Rappel du filtre */}
            <div className={`${card} flex flex-wrap items-center gap-3 px-4 py-2`}>
                <Filter className="h-4 w-4 text-primary"/>
                <span className={`text-sm font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}>
                    {t("productFilter.viewTitle")}
                </span>
                <div className="min-w-0 flex-1">
                    <SelectionChips selection={selection} isDarkMode={isDarkMode}
                                    onRemove={(item) => onChange(selection.filter((x) => !(x.type === item.type && x.id === item.id)))}/>
                </div>
                <Button variant="plain" icon={Download} compact disabled={!stats} onClick={exportCsv}>
                    {t("productFilter.exportCsv")}
                </Button>
                <Button variant="plain" icon={X} compact onClick={() => onChange([])}>
                    {t("productFilter.clearFilter")}
                </Button>
            </div>

            {current.isLoading && (
                <div className="flex flex-col items-center justify-center py-16">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-teal-200 border-t-teal-500"/>
                    <p className="mt-3 text-sm text-gray-500">{t("dashboard.loading")}</p>
                </div>
            )}

            {current.error && !current.isLoading && (
                <p className={`border-b ${line} bg-red-500/10 px-4 py-3 text-sm text-red-600`}>{t("productFilter.error")}</p>
            )}

            {s && (
                <>
                    <div className={`grid grid-cols-2 gap-px border-b md:grid-cols-3 xl:grid-cols-6 ${
                        isDarkMode ? "border-gray-700 bg-gray-700" : "border-gray-200 bg-gray-200"}`}>
                        <Kpi label={t("productFilter.kpi.net")} value={s.net} previous={prev("net")} format={money}
                             hint={t("productFilter.kpi.netHint", {gross: money(s.gross)})} isDarkMode={isDarkMode}/>
                        <Kpi label={t("productFilter.kpi.discount")} value={s.discount} previous={prev("discount")} format={money}
                             invert isDarkMode={isDarkMode}/>
                        <Kpi label={t("productFilter.kpi.quantity")} value={s.quantity} previous={prev("quantity")}
                             format={formatNumber} isDarkMode={isDarkMode}/>
                        <Kpi label={t("productFilter.kpi.tickets")} value={s.tickets} previous={prev("tickets")}
                             format={formatNumber} isDarkMode={isDarkMode}/>
                        <Kpi label={t("productFilter.kpi.averagePrice")} value={s.average_price} previous={prev("average_price")}
                             format={money} isDarkMode={isDarkMode}/>
                        <Kpi label={t("productFilter.kpi.share")} value={s.share_of_revenue} previous={prev("share_of_revenue")}
                             format={formatPercent} hint={t("productFilter.kpi.shareHint", {total: money(s.revenue_all)})}
                             isDarkMode={isDarkMode}/>
                    </div>

                    <div className={card}>
                        <div className={`flex items-center gap-2 border-b px-4 py-3 ${line}`}>
                            <BarChart3 className="h-5 w-5 text-teal-500"/>
                            <h2 className={`text-sm font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}>
                                {t("productFilter.chart.title")}
                            </h2>
                        </div>
                        <div className="p-2">
                            {chartData ? (
                                <Chart chartType="ComboChart" width="100%" height="300px" data={chartData}
                                       options={{
                                           backgroundColor: "transparent",
                                           legend: {position: "top", textStyle: {color: isDarkMode ? "#D1D5DB" : "#374151"}},
                                           seriesType: "bars",
                                           series: {0: {color: "#4DB6AC", targetAxisIndex: 0}, 1: {type: "line", color: "#F59E0B", targetAxisIndex: 1}},
                                           vAxes: {0: {format: "#,### DH"}, 1: {format: "#"}},
                                           hAxis: {textStyle: {color: isDarkMode ? "#9CA3AF" : "#6B7280"}},
                                           vAxis: {textStyle: {color: isDarkMode ? "#9CA3AF" : "#6B7280"}},
                                           chartArea: {width: "85%", height: "70%"},
                                       }}/>
                            ) : (
                                <p className="py-12 text-center text-sm text-gray-500">{t("productFilter.noSales")}</p>
                            )}
                        </div>
                    </div>

                    <div className={card}>
                        <div className={`flex gap-1 border-b px-2 ${line}`} role="tablist">
                            {[["products", t("productFilter.byProduct"), Package, stats.products.length],
                                ["categories", t("productFilter.byCategory"), Tag, stats.categories.length]].map(([key, label, Icon, n]) => (
                                <button key={key} type="button" role="tab" aria-selected={tableTab === key} onClick={() => setTableTab(key)}
                                        className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium ${
                                            tableTab === key ? "border-primary text-primary" : "border-transparent text-gray-500"}`}>
                                    <Icon className="h-4 w-4"/> {label} <span className="text-xs text-gray-400">({n})</span>
                                </button>
                            ))}
                        </div>
                        <StatsTable isDarkMode={isDarkMode} emptyLabel={t("productFilter.noSales")}
                                    rows={tableTab === "products" ? stats.products : stats.categories}
                                    columns={tableTab === "products" ? productColumns : categoryColumns}/>
                    </div>

                    <p className="px-4 py-3 text-xs text-gray-500">{t("productFilter.scopeNote")}</p>
                </>
            )}
        </section>
    );
};

export default ProductStatsView;
